import React from 'react';
import { useParams } from 'react-router-dom';
import AuthLayout from '../../components/layout/AuthLayout';
import { useGetOrganizationKPIsQuery } from '../../api/organizations.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import { formatNumbers } from '../../utils/formatNumbers.js';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import BaseChart from '../../components/form/BaseChart.jsx';
import KPIsIncomeStatementCarousel from '../../components/misc/KPIsIncomeStatementCarousel.jsx';
import KPIsBalanceSheetCarousel from '../../components/misc/KPIsBalanceSheetCarousel.jsx';
import { formatCurrencyOrDash, getSortedValuesByYear } from '../../utils/helper.js';

const OrganizationKPIs = () => {
    const { id } = useParams();
    const { data, isLoading } = useGetOrganizationKPIsQuery({ id, interval: null });

    if (isLoading) {
        return (
            <AuthLayout title='Organization KPIs' backRoute={`/organizations/${id}`}>
                <ThreeDotsLoader className='mt-6' />
            </AuthLayout>
        );
    }

    if (!data) {
        return (
            <AuthLayout title='Organization KPIs' backRoute={`/organizations/${id}`}>
                <div className='text-center mt-6'>Organization KPIs not found.</div>
            </AuthLayout>
        );
    }

    const kpis = data?.response?.stock_data?.FundamentalData;

    const defaultLogarithmicOptions = (title, showLegend = true) => {
        return {
            responsive: true,
            aspectRatio: 1.3,
            plugins: {
                legend: {
                    display: showLegend,
                    position: 'top'
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    type: 'logarithmic',
                    title: {
                        display: true,
                        text: title
                    }
                }
            }
        };
    };

    const pieLogarithmicOptions = (showLegend = true) => {
        return {
            responsive: true,
            aspectRatio: 0.6,
            plugins: {
                legend: {
                    display: showLegend,
                    position: 'top'
                }
            },
            title: {
                display: false
            },
            scales: {
                y: {
                    beginAtZero: true,
                    type: 'logarithmic'
                }
            }
        };
    };

    const lineLogarithmicOptions = () => {
        return {
            stacked: false,
            maintainAspectRatio: false,
            aspectRatio: 0.6,
            scales: {
                y: {
                    beginAtZero: true,
                    type: 'logarithmic',
                    display: true,
                    position: 'left'
                }
            }
        };
    };

    const sidewaysBarLogarithmicOptions = (title) => {
        return {
            indexAxis: 'y',
            maintainAspectRatio: false,
            aspectRatio: 0.6,
            plugins: {
                legend: {
                    position: 'top'
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    type: 'logarithmic'
                },
                y: {
                    title: {
                        display: true,
                        text: title
                    },
                    grid: {
                        display: false
                    }
                }
            }
        };
    };

    const polarAreaOptions = () => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { position: 'top' },
                tooltip: {
                    callbacks: {
                        label: function (tooltipItem) {
                            return `EPS: ${tooltipItem.raw.toFixed(2)}`;
                        }
                    }
                },
                datalabels: {
                    anchor: 'end',
                    align: 'end',
                    formatter: (value) => value.toFixed(2)
                }
            }
        };
    };

    const overviewData = (overviewData) => {
        if (!overviewData || overviewData.length === 0) {
            return (
                <BaseCard className='p-4 mb-6 bg-theme1-white'>
                    <div className='flex items-center gap-4'>
                        There is no information about the Overview of this symbol presently.
                    </div>
                </BaseCard>
            );
        }

        return (
            <>
                <BaseCard className='p-4 mb-6 bg-theme1-white'>
                    <div className='flex items-center gap-4'>
                        <div className='w-16 h-16 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-2xl flex-shrink-0'>
                            {overviewData.Symbol}
                        </div>
                        <div>
                            <h4 className='font-bold leading-[25px] text-[22px] text-theme1-primary'>
                                {overviewData.Name}
                            </h4>
                            <div className='text-md text-theme1-text2 capitalize font-bold mt-[2px]'>
                                {overviewData.Industry}
                            </div>
                            <div className='text-sm text-theme1-text3 capitalize mt-[2px]'>
                                {overviewData.Technology}
                            </div>
                            <div className='text-sm text-theme1-text1 capitalize mt-[2px]'>{overviewData.Country}</div>
                        </div>
                    </div>
                    {overviewData.Description !== null && overviewData.Description !== 'None' && (
                        <div className='mt-3 text-[15px] text-theme1-primary2'>{overviewData.Description}</div>
                    )}
                </BaseCard>

                <div className='grid sm:grid-cols-2 md:grid-cols-3 gap-4'>
                    {[
                        { text: overviewData.Exchange, title: 'Exchange', textSize: 40 },
                        { text: overviewData.Sector, title: 'Sector', textSize: 20 },
                        {
                            text: formatNumbers(Number(overviewData.MarketCapitalization), '$'),
                            title: 'Market Cap',
                            textSize: 40
                        }
                    ].map(({ text, title, textSize }) => (
                        <BaseCard
                            key={title + '-' + text}
                            className='p-4 py-8 content-center rounded-4xl shadow-mildSubtle text-center'
                        >
                            <div className={`font-bold text-theme1-gold text-[${textSize}px] leading-none`}>{text}</div>
                            <div className='font-bold text-theme1-title2 mt-1'>{title}</div>
                        </BaseCard>
                    ))}
                </div>

                <BaseChart
                    type='bar'
                    id='financial-overview'
                    title='Financial Overview'
                    options={defaultLogarithmicOptions('', false)}
                    childClassName='bg-theme1-white p-4 shadow-md rounded-2xl'
                    className='mt-6 mb-6 shadow-md rounded-2xl'
                    data={{
                        labels: ['Revenue', 'Profit Margin', 'Operating Margin', 'ROA', 'ROE'],
                        datasets: [
                            {
                                label: 'Financial Performance',
                                backgroundColor: ['#D47554', '#CC8F49', '#A05514', '#6A4E4E', '#8D6C6C'],
                                data: [
                                    Number(overviewData.RevenueTTM),
                                    Number(overviewData.ProfitMargin),
                                    Number(overviewData.OperatingMarginTTM),
                                    Number(overviewData.ReturnOnAssetsTTM),
                                    Number(overviewData.ReturnOnEquityTTM)
                                ]
                            }
                        ]
                    }}
                />

                <div className='mt-6'>
                    <h3 className='text-lg font-bold text-theme1-primary mb-2'>Key Metrics</h3>

                    {/* Desktop table view */}
                    <div className='hidden md:block'>
                        <DataTable value={[overviewData]} stripedRows showGridlines className='w-full'>
                            {[
                                {
                                    field: 'EPS',
                                    header: 'EPS',
                                    format: (value) => formatCurrencyOrDash(value)
                                },
                                { field: 'PEGRatio', header: 'PEG Ratio' },
                                { field: 'BookValue', header: 'Book Value' },
                                { field: 'TrailingPE', header: 'Trailing P/E' },
                                { field: 'ForwardPE', header: 'Forward P/E' },
                                {
                                    field: 'DividendPerShare',
                                    header: 'Dividend Per Share',
                                    format: (value) => formatCurrencyOrDash(value)
                                },
                                {
                                    field: 'DividendYield',
                                    header: 'Dividend Yield',
                                    format: (value) =>
                                        value !== null &&
                                        value !== 'None' &&
                                        value !== undefined &&
                                        !isNaN(Number(value))
                                            ? value + '%'
                                            : '-'
                                }
                            ].map(({ field, header, format }) => (
                                <Column
                                    key={field}
                                    headerClassName='bg-theme1-pinkGray border border-theme1-pale text-theme1-dark'
                                    className='border border-theme1-pale'
                                    field={field}
                                    header={header}
                                    body={format ? (row) => format(row[field]) : undefined}
                                />
                            ))}
                        </DataTable>
                    </div>

                    {/* Mobile stacked view */}
                    <div className='md:hidden'>
                        <div className='bg-white p-4 rounded-lg shadow-md'>
                            {[
                                {
                                    field: 'EPS',
                                    header: 'EPS',
                                    format: (value) => formatCurrencyOrDash(value)
                                },
                                { field: 'PEGRatio', header: 'PEG Ratio' },
                                { field: 'BookValue', header: 'Book Value' },
                                { field: 'TrailingPE', header: 'Trailing P/E' },
                                { field: 'ForwardPE', header: 'Forward P/E' },
                                {
                                    field: 'DividendPerShare',
                                    header: 'Dividend Per Share',
                                    format: (value) => formatCurrencyOrDash(value)
                                },
                                {
                                    field: 'DividendYield',
                                    header: 'Dividend Yield',
                                    format: (value) =>
                                        value !== null &&
                                        value !== 'None' &&
                                        value !== undefined &&
                                        !isNaN(Number(value))
                                            ? value + '%'
                                            : '-'
                                }
                            ].map(({ field, header, format }) => (
                                <div key={field} className='flex justify-between py-2 border-b last:border-none'>
                                    <span className='font-semibold'>{header}:</span>
                                    <span className='float-right'>
                                        {format ? format(overviewData[field]) : overviewData[field]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const incomeStatements = (incomeStatement) => {
        if (!incomeStatement || incomeStatement.length === 0) {
            return (
                <BaseCard className='p-4 mb-6 bg-theme1-white'>
                    <div className='flex items-center gap-4'>
                        There is no information about the Income Statements of this symbol presently.
                    </div>
                </BaseCard>
            );
        }

        const incomeAsc = getSortedValuesByYear(incomeStatement);
        const incomeDesc = [...incomeAsc].reverse();

        const chartYears = incomeAsc.map((entry) => entry.fiscalDateEnding.substring(0, 4));

        const tableYears = incomeDesc.map((entry) => entry.fiscalDateEnding.substring(0, 4));

        const depreciationAndAmortizationDesc = incomeDesc.map((entry) => Number(entry.depreciationAndAmortization));
        const investmentIncomeNetDesc = incomeDesc.map((entry) => Number(entry.investmentIncomeNet) || 0);
        const netInterestIncomeDesc = incomeDesc.map((entry) => Number(entry.netInterestIncome) || 0);
        const otherNonOperatingIncomeDesc = incomeDesc.map((entry) => Number(entry.otherNonOperatingIncome) || 0);

        return (
            <div className='mt-6'>
                <h3 className='text-lg font-bold text-theme1-primary mb-2'>Income Statements</h3>

                <KPIsIncomeStatementCarousel
                    years={chartYears}
                    incomeStatement={incomeAsc}
                    lineLogarithmicOptions={lineLogarithmicOptions}
                    sidewaysBarLogarithmicOptions={sidewaysBarLogarithmicOptions}
                    defaultLogarithmicOptions={defaultLogarithmicOptions}
                />

                <div className='mt-6'>
                    <h4 className='text-lg font-bold text-theme1-primary mb-2'>Cash Flow Indicators</h4>

                    {/* Desktop table view (highest -> lowest) */}
                    <div className='hidden md:block'>
                        <DataTable
                            value={tableYears.map((year, i) => ({
                                year,
                                depreciation: depreciationAndAmortizationDesc[i],
                                investmentIncome: investmentIncomeNetDesc[i],
                                netInterestIncome: netInterestIncomeDesc[i],
                                otherIncome: otherNonOperatingIncomeDesc[i]
                            }))}
                            stripedRows
                            showGridlines
                            className='w-full'
                        >
                            {[
                                { field: 'year', header: 'Year' },
                                {
                                    field: 'depreciation',
                                    header: 'Depreciation & Amortization',
                                    format: (value) => formatCurrencyOrDash(value)
                                },
                                {
                                    field: 'investmentIncome',
                                    header: 'Investment Income',
                                    format: (value) => formatCurrencyOrDash(value)
                                },
                                {
                                    field: 'netInterestIncome',
                                    header: 'Net Interest Income',
                                    format: (value) => formatCurrencyOrDash(value)
                                },
                                {
                                    field: 'otherIncome',
                                    header: 'Other Non-Operating Income',
                                    format: (value) => formatCurrencyOrDash(value)
                                }
                            ].map(({ field, header, format }) => (
                                <Column
                                    key={field}
                                    headerClassName='bg-theme1-pinkGray border border-theme1-pale text-theme1-primary2'
                                    className='border border-theme1-pale'
                                    field={field}
                                    header={header}
                                    body={format ? (row) => format(row[field]) : undefined}
                                />
                            ))}
                        </DataTable>
                    </div>

                    {/* Mobile stacked view (highest -> lowest) */}
                    <div className='md:hidden'>
                        {tableYears.map((year, i) => (
                            <div key={year} className='bg-white p-4 rounded-lg shadow-md mb-4'>
                                <h5 className='text-theme1-primary text-[20px] font-bold mb-2'>{year}</h5>
                                <div className='flex justify-between py-2 border-b last:border-none'>
                                    <span className='font-semibold'>Depreciation & Amortization:</span>
                                    <span className='float-right'>
                                        {formatCurrencyOrDash(depreciationAndAmortizationDesc[i])}
                                    </span>
                                </div>
                                <div className='flex justify-between py-2 border-b last:border-none'>
                                    <span className='font-semibold'>Investment Income:</span>
                                    <span className='float-right'>
                                        {formatCurrencyOrDash(investmentIncomeNetDesc[i])}
                                    </span>
                                </div>
                                <div className='flex justify-between py-2 border-b last:border-none'>
                                    <span className='font-semibold'>Net Interest Income:</span>
                                    <span className='float-right'>
                                        {formatCurrencyOrDash(netInterestIncomeDesc[i])}
                                    </span>
                                </div>
                                <div className='flex justify-between py-2 border-b last:border-none'>
                                    <span className='font-semibold'>Other Non-Operating Income:</span>
                                    <span className='float-right'>
                                        {formatCurrencyOrDash(otherNonOperatingIncomeDesc[i])}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const earnings = (earningsArr) => {
        if (!earningsArr || earningsArr.length === 0) {
            return (
                <BaseCard className='p-4 mb-6 bg-theme1-white'>
                    <div className='flex items-center gap-4'>
                        There is no information about the Earnings of this symbol presently.
                    </div>
                </BaseCard>
            );
        }

        const earningsAsc = getSortedValuesByYear(earningsArr);
        const years = earningsAsc.map((entry) => entry.fiscalDateEnding.substring(0, 4));
        const epsValues = earningsAsc.map((e) => Number(e.reportedEPS));

        return (
            <div className='mt-6'>
                <h3 className='text-lg font-bold text-theme1-primary mb-2'>Earnings</h3>
                <BaseChart
                    type='polarArea'
                    id='earnings'
                    title='EPS Overview'
                    options={polarAreaOptions()}
                    childClassName='bg-theme1-white p-6 shadow-md rounded-2xl'
                    className='mb-6 shadow-md rounded-2xl'
                    data={{
                        labels: years,
                        datasets: [
                            {
                                data: epsValues,
                                backgroundColor: [
                                    'rgba(238, 109, 109, 0.7)',
                                    'rgba(102, 162, 255, 0.7)',
                                    'rgba(255, 162, 57, 0.7)',
                                    'rgba(160, 215, 135, 0.7)',
                                    'rgba(150, 115, 225, 0.7)'
                                ],
                                borderColor: [
                                    'rgba(238, 109, 109, 1)',
                                    'rgba(102, 162, 255, 1)',
                                    'rgba(255, 162, 57, 1)',
                                    'rgba(160, 215, 135, 1)',
                                    'rgba(150, 115, 225, 1)'
                                ],
                                borderWidth: 2
                            }
                        ]
                    }}
                />
            </div>
        );
    };

    const cashFlow = (cashFlowData) => {
        if (!cashFlowData || cashFlowData.length === 0) {
            return (
                <BaseCard className='p-4 mb-6 bg-theme1-white'>
                    <div className='flex items-center gap-4'>
                        There is no information about the Cash Flow of this symbol presently.
                    </div>
                </BaseCard>
            );
        }

        const cfAsc = getSortedValuesByYear(cashFlowData);
        const cfDesc = [...cfAsc].reverse();

        const chartYears = cfAsc.map((entry) => entry.fiscalDateEnding.substring(0, 4));
        const tableYears = cfDesc.map((entry) => entry.fiscalDateEnding.substring(0, 4));

        return (
            <div className='mt-6'>
                <h3 className='text-lg font-bold text-theme1-primary mb-2'>Cash Flow Overview</h3>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    {cfAsc.map((yearItem, i) => {
                        const year = chartYears[i];
                        return (
                            <BaseChart
                                key={year}
                                type='doughnut'
                                id={`cash-flow-doughnut-${year}`}
                                title={`Cash Flow - ${year}`}
                                chartTitle={false}
                                options={pieLogarithmicOptions(true)}
                                className='bg-theme1-white p-4 shadow-md rounded-2xl'
                                data={{
                                    labels: [
                                        'Operating Cash Flow',
                                        'Investing Cash Flow',
                                        'Financing Cash Flow',
                                        'Net Income'
                                    ],
                                    datasets: [
                                        {
                                            data: [
                                                Number(yearItem.operatingCashflow),
                                                Number(yearItem.cashflowFromInvestment),
                                                Number(yearItem.cashflowFromFinancing),
                                                Number(yearItem.netIncome)
                                            ],
                                            backgroundColor: ['#4E86DE', '#879D00', '#EE6D6D', '#FFA239']
                                        }
                                    ]
                                }}
                            />
                        );
                    })}
                </div>

                {/* Desktop table view desc */}
                <div className='hidden md:block mt-6'>
                    <DataTable
                        value={tableYears.map((year, i) => {
                            const item = cfDesc[i];
                            return {
                                year,
                                operatingCashflow: item.operatingCashflow,
                                investingCashFlow: item.cashflowFromInvestment,
                                financingCashFlow: item.cashflowFromFinancing,
                                capitalExpenditures: item.capitalExpenditures,
                                netIncome: item.netIncome,
                                dividendPayout: item.dividendPayout
                            };
                        })}
                        stripedRows
                        showGridlines
                        className='w-full'
                    >
                        {[
                            { field: 'year', header: 'Year' },
                            {
                                field: 'operatingCashflow',
                                header: 'Operating Cash Flow',
                                format: (value) => formatCurrencyOrDash(value)
                            },
                            {
                                field: 'investingCashFlow',
                                header: 'Investing Cash Flow',
                                format: (value) => formatCurrencyOrDash(value)
                            },
                            {
                                field: 'financingCashFlow',
                                header: 'Financing Cash Flow',
                                format: (value) => formatCurrencyOrDash(value)
                            },
                            {
                                field: 'capitalExpenditures',
                                header: 'Capital Expenses',
                                format: (value) => formatCurrencyOrDash(value)
                            },
                            {
                                field: 'netIncome',
                                header: 'Net Income',
                                format: (value) => formatCurrencyOrDash(value)
                            },
                            {
                                field: 'dividendPayout',
                                header: 'Dividend Payout',
                                format: (value) => formatCurrencyOrDash(value)
                            }
                        ].map(({ field, header, format }) => (
                            <Column
                                key={field}
                                headerClassName='bg-theme1-pinkGray border border-theme1-pale text-theme1-primary2'
                                className='border border-theme1-pale'
                                field={field}
                                header={header}
                                body={format ? (row) => format(row[field]) : undefined}
                            />
                        ))}
                    </DataTable>
                </div>

                {/* Mobile stacked view desc */}
                <div className='md:hidden mt-6'>
                    {tableYears.map((year, i) => {
                        const item = cfDesc[i];
                        return (
                            <div key={year} className='bg-white p-4 rounded-lg shadow-md mb-4'>
                                <h5 className='text-theme1-primary text-[20px] font-bold mb-2'>{year}</h5>
                                {[
                                    {
                                        label: 'Operating Cash Flow',
                                        value: item.operatingCashflow
                                    },
                                    {
                                        label: 'Investing Cash Flow',
                                        value: item.cashflowFromInvestment
                                    },
                                    {
                                        label: 'Financing Cash Flow',
                                        value: item.cashflowFromFinancing
                                    },
                                    {
                                        label: 'Capital Expenses',
                                        value: item.capitalExpenditures
                                    },
                                    {
                                        label: 'Net Income',
                                        value: item.netIncome
                                    },
                                    {
                                        label: 'Dividend Payout',
                                        value: item.dividendPayout
                                    }
                                ].map((rowItem) => (
                                    <div
                                        key={rowItem.label}
                                        className='flex justify-between py-2 border-b last:border-none'
                                    >
                                        <span className='font-semibold'>{rowItem.label}:</span>
                                        <span className='float-right'>{formatCurrencyOrDash(rowItem.value)}</span>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const balanceSheet = (balanceSheetData) => {
        if (!balanceSheetData || balanceSheetData.length === 0) {
            return (
                <BaseCard className='p-4 mb-6 bg-theme1-white'>
                    <div className='flex items-center gap-4'>
                        There is no information about the Balance Sheet of this symbol presently.
                    </div>
                </BaseCard>
            );
        }

        const bsAsc = getSortedValuesByYear(balanceSheetData);
        const chartYears = bsAsc.map((entry) => entry.fiscalDateEnding.substring(0, 4));

        return (
            <div className='mt-6'>
                <h3 className='text-lg font-bold text-theme1-primary mb-2'>Balance Sheet Breakdown</h3>
                <KPIsBalanceSheetCarousel
                    years={chartYears}
                    balanceSheet={bsAsc}
                    lineLogarithmicOptions={lineLogarithmicOptions}
                    defaultLogarithmicOptions={defaultLogarithmicOptions}
                />
            </div>
        );
    };

    return (
        <AuthLayout title='Organization KPIs' backRoute={`/organizations/${id}`}>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-10'>
                {kpis.OVERVIEW.length === 0 &&
                kpis.INCOME_STATEMENT.length === 0 &&
                kpis.EARNINGS.length === 0 &&
                kpis.CASH_FLOW.length === 0 &&
                kpis.BALANCE_SHEET.length === 0 ? (
                    <div className='flex items-center gap-4'>
                        Not enough information about the Organization KPIs was found.
                    </div>
                ) : (
                    <>
                        {overviewData(kpis.OVERVIEW)}
                        {incomeStatements(kpis.INCOME_STATEMENT)}
                        {earnings(kpis.EARNINGS)}
                        {cashFlow(kpis.CASH_FLOW)}
                        {balanceSheet(kpis.BALANCE_SHEET)}
                    </>
                )}
            </div>
        </AuthLayout>
    );
};

export default OrganizationKPIs;
